import { useQuery } from 'react-query';
import { useMemo } from 'react';
import { get } from '../../../utils/api';
import AffordableWaitlistService from '../../../services/affordableWaitlistService';

export function useFetchAffordableWaitlist(
  organizationId: string,
  propertyId: string,
  applicationId: string,
) {
  const affordableWaitlistService = new AffordableWaitlistService();
  const queryKey = ['getAffordableWaitlistByApplicationId', applicationId];
  const { data, error, isLoading, refetch } = useQuery(
    queryKey,
    () =>
      affordableWaitlistService.getAffordableWaitlistByApplicationId(
        organizationId,
        propertyId,
        applicationId,
      ),
    {
      enabled: Boolean(
        organizationId.length && propertyId.length && applicationId.length,
      ),
      refetchOnWindowFocus: false,
    },
  );

  const { dataValue, hasData } = useMemo(() => {
    return {
      hasData: isLoading === false && Object.keys(data ?? {})?.length > 0,
      dataValue: data?.data ?? {},
    };
  }, [data, isLoading]);

  return { data: dataValue, error, isLoading, hasData, refetch };
}

export function useProviderPropertyProfiles({ propertyId, enabled }) {
  const { data, isLoading } = useQuery(
    ['providerPropertyProfiles', propertyId],
    () => {
      return get(
        `/screening-service/provider-property-profiles/properties/${propertyId}`,
      );
    },
    {
      enabled,
      refetchOnWindowFocus: false,
    },
  );

  return {
    providerPropertyProfiles: data?.result,
    isProviderPropertyProfilesLoading: isLoading,
  };
}

export function useScreeningMethodsByProperty({ propertyId, enabled }) {
  const { data, isLoading } = useQuery(
    ['screeningMethodsByProperty', propertyId],
    () => get(`/screening-service/screening-methods/by-property/${propertyId}`),
    { enabled, refetchOnWindowFocus: false },
  );

  return {
    screeningMethods: data?.result ?? [],
    isScreeningMethodsLoading: isLoading,
  };
}

export function useScreeningPurposes() {
  const { data, isLoading } = useQuery(
    ['screeningPurposes'],
    () => get('/screening-service/screening-purposes'),
    { refetchOnWindowFocus: false },
  );

  return {
    screeningPurposes: data?.result ?? [],
    isScreeningPurposesLoading: isLoading,
  };
}
